import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Download, RefreshCw, CheckCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VideoHistory from './VideoHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProgressBar = ({ progress, label }) => (
  <div className="w-full space-y-2">
    <div className="flex justify-between text-xs text-gray-500">
      <span>{label}</span>
      <span>{progress}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const VideoTranslatePage = () => {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState('');
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [status, setStatus] = useState('idle');
  const [resultUrl, setResultUrl] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [recordId, setRecordId] = useState(null);

  useEffect(() => { loadLanguages(); }, []);

  const loadLanguages = async () => {
    try {
      const res = await axios.get(`${API}/heygen/languages`);
      const langs = res.data.languages || [];
      setLanguages(langs);
      if (langs.length > 0) setSelectedLang(langs[0].code || langs[0]);
    } catch (_) {}
  };

  const startProgress = () => {
    setProgress(10);
    const steps = [
      { at: 10000, val: 20 }, { at: 30000, val: 35 }, { at: 60000,  val: 50 },
      { at: 120000, val: 65 }, { at: 180000, val: 78 }, { at: 300000, val: 90 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(v => Math.max(v, s.val)), s.at));
  };

  const translate = async () => {
    if (!videoUrl.trim()) { toast.error('Please enter a video URL'); return; }
    if (!selectedLang) { toast.error('Please select a language'); return; }
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please sign in first'); return; }
    setStatus('translating'); setStatusMsg('Submitting translation request...'); setErrorMsg(''); setProgress(5);
    try {
      const res = await axios.post(`${API}/heygen/translate`, {
        video_url: videoUrl.trim(), target_language: selectedLang, title: 'Translated Video'
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success && res.data.translate_id) {
        setRecordId(res.data.record_id);
        setStatus('polling'); setStatusMsg('Translation in progress...');
        startProgress();
        pollTranslate(res.data.translate_id, res.data.record_id);
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.response?.data?.detail || 'Translation failed. Please check the video URL and try again.');
    }
  };

  const pollTranslate = (id, recId) => {
    let n = 0;
    const t = setInterval(async () => {
      n++;
      if (n > 180) { clearInterval(t); setStatus('error'); setErrorMsg('Timed out. Please try again.'); return; }
      try {
        const res = await axios.get(`${API}/heygen/translate-status/${id}`);
        const { status: s, video_url } = res.data;
        if ((s === 'success' || s === 'completed') && video_url) {
          clearInterval(t); setProgress(100); setResultUrl(video_url); setStatus('done');
          toast.success('Translation complete!');
          if (recId) {
            const token = localStorage.getItem('token');
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'completed', video_url },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else if (s === 'failed') {
          clearInterval(t); setStatus('error');
          setErrorMsg('Translation failed. Please ensure the video URL is publicly accessible.');
          if (recId) {
            const token = localStorage.getItem('token');
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'failed' },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else {
          setStatusMsg(`Translating your video... (${s})`);
          setProgress(v => Math.max(v, Math.min(90, 10 + n * 0.8)));
        }
      } catch (_) {}
    }, 5000);
  };

  const reset = () => { setVideoUrl(''); setStatus('idle'); setResultUrl(null); setStatusMsg(''); setErrorMsg(''); setProgress(0); setRecordId(null); };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = resultUrl; a.download = 'translated-video.mp4'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/creator-ai/heygen-hub')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-1.5 text-xs sm:text-sm">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Video Translate</h1>
              <p className="text-xs text-gray-500">Dub any video into another language with lip-sync</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-8 space-y-4">

        {/* IDLE */}
        {status === 'idle' && (
          <>
            <VideoHistory videoType="translate" accentColor="emerald" />
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Translate Any Video</h2>
              <p className="text-gray-500 text-xs sm:text-sm">Paste a video URL, choose a language, and get a dubbed version with lip-sync.</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">🔗 Video URL</label>
                <input type="url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                  placeholder="https://example.com/video.mp4"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                <p className="text-xs text-gray-400 mt-1">Must be a publicly accessible video URL.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">🌍 Translate To</label>
                <div className="relative">
                  <select value={selectedLang} onChange={e => setSelectedLang(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white appearance-none pr-10">
                    {languages.map(l => <option key={l.code||l} value={l.code||l}>{l.language||l}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <button onClick={translate} disabled={!videoUrl.trim() || !selectedLang}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-semibold py-3.5 rounded-xl hover:opacity-90 active:opacity-80 disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                <Globe className="h-4 w-4" /> Start Translation
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">ℹ️ Tips</p>
              <div className="space-y-1.5">
                {['📹 Works with MP4, MOV direct URLs','🌐 Supports YouTube, Twitter/X links','⏱️ Best for videos under 5 minutes','🔊 Lip-sync preserved in translation'].map((tip,i)=>(
                  <p key={i} className="text-xs text-gray-600">{tip}</p>
                ))}
              </div>
            </div>
          </>
        )}

        {/* TRANSLATING / POLLING */}
        {(status === 'translating' || status === 'polling') && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Translating Your Video</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{statusMsg}</p>
              </div>
            </div>
            <ProgressBar progress={progress} label="Translating..." />
            <div className="bg-gray-50 rounded-xl px-4 py-3 space-y-1">
              <p className="text-xs text-gray-500">✅ Audio transcribed</p>
              <p className="text-xs text-gray-500">⚙️ Translating speech...</p>
              <p className="text-xs text-gray-500">🎙️ Regenerating voice with lip-sync</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-800">⏱ Average time: <strong>3–8 minutes</strong>. Please keep this page open.</p>
            </div>
          </div>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-red-200 p-6 text-center space-y-4">
            <p className="text-red-600 font-medium text-sm">{errorMsg}</p>
            <button onClick={reset} className="bg-gray-900 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto text-sm">
              <RefreshCw className="h-4 w-4" /> Try Again
            </button>
          </div>
        )}

        {/* DONE */}
        {status === 'done' && resultUrl && (
          <div className="bg-white rounded-2xl border border-green-200 p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold text-base">Translation Complete!</span>
            </div>
            <div className="bg-black rounded-xl overflow-hidden">
              <video
                src={resultUrl} controls muted playsInline
                className="w-full max-h-[300px] sm:max-h-[420px]"
                onError={() => toast.error('Preview failed — use the Open button to watch your video')}
              />
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={handleDownload}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm">
                <Download className="h-4 w-4" /> Download Translated Video
              </button>
              <a href={resultUrl} target="_blank" rel="noreferrer"
                className="w-full border-2 border-emerald-300 text-emerald-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-emerald-50 text-sm">
                <ExternalLink className="h-4 w-4" /> Open in Browser
              </a>
              <button onClick={reset}
                className="w-full border-2 border-gray-200 text-gray-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                <RefreshCw className="h-4 w-4" /> Translate Another
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VideoTranslatePage;
